/**
 * Collection of data structures for all nodes in the AST.
 * 
 * Each node is an Interface. The first implementation was a class but was made an interface to
 * be more easily serializable and allow for incremental changes to the AST.
 */

import { DataType, InternalDataRepresentation } from "./interpreter";

export interface Interpretable {
    type: InterpType,
    data?: Record<string, any>,
    children?: string[] | {[key: string]: string | string[]},
    parent: string|null
}

/**
 * This represents an array definition, but does not represent an array in the stack.
 * As such, the elements in the array are all Interpretables- they're waiting to be interpreted.
 * 
 * Examples:
 * - `x = [1, 2, 3]`
 * - `print([a, 2, otherVar])`
 * - `arr = [1, func(), 'abc']`
 * 
 * These are encompassed by an ArrayLiteralNode.
 */
export interface ArrayLiteral extends Interpretable {
    type: InterpType.ArrayLiteral,
    children: string[]
}

/**
 * Represents an assignment to a variable.
 * Examples:
 * - `x = 1`,
 * - `test = '1234'`
 * - `a = b`
 * - `id = [1, 2, 3, 4]`
 * 
 * The left is an Identifier, the right is any Interpretable.
 */
export interface Assignment extends Interpretable {
    type: InterpType.Assignment,
    children: {
        /**
         * The left side of the assignment (what variable to assign to).
         * 
         * Example:
         *  - `a = b`, `a` is `identifier` and `b` is `children.right`.
         * 
         */
        left: string

        /** 
         * A list of all subscripts on the left side of the identifier. Only used when the left
         * side of the assignment is an array.
         * 
         * Example:
         *  - `a[1][b][c] = data`, identifierSubscripts would hold uuids for `1`, `b`, and `c`.
         */
        identifierSubscripts?: string[],
        /** 
         * The right side of the assignment (the data to be assigned to a variable).
         * 
         * Example:
         *  - `a = b`, `a` is `identifier` and `b` is `children.right`.
         */
        right: string
    }
}

/**
 * Any binary operator allowing for arbitrary data on either side.
 */
export interface BinaryGeneralOperator extends Interpretable {
    type: InterpType.BinaryGeneralOp,
    data: {
        operator: '==' | '!=' | 'or' | 'and'
    }
    children: {
        left: string,
        right: string
    }
}

/**
 * Any binary operator requiring two numbers on either side.
 */
export interface BinaryNumericalOperator extends Interpretable {
    type: InterpType.BinaryNumericalOp,
    data: {
        operator: '<' | '<=' | '>=' | '>' | '+' | '-' | '*' | '**' | '/'  | '%'
    }
    children: {
        left: string,
        right: string
    }
}

/**
 * Represents multiple statements to be executed in a row.
 * All programs start with a BlockNode at their root.
 * 
 * BlockNodes do not control the scope: that is the job of other nodes.
 */
export interface Block extends Interpretable {
    type: InterpType.Block,
    children: string[]
}

/**
 * Breaks out of a for or while loop.
 */
export interface Break extends Interpretable {
    type: InterpType.Break,
}

/**
 * Represents a call to a function with arguments.
 */
export interface Call extends Interpretable {
    type: InterpType.Call,
    children: {
        /** The identifier of the function to call */
        identifier: string
        /** What interpretables go into the function arguments */
        arguments: string[]
    }
}

/**
 * Not to be confused with Data, this node is specifically made to encapsulate data
 * literals in the code.
 * 
 * Example: in `x = 10`, 10 is a literal.
 * Example: in `x = 'hi'`, 'hi' is a literal.
 * 
 * This does not hold array literals - since array literals have children, they are best as
 * another node.
 */
export interface DataLiteral extends Interpretable {
    type: InterpType.DataLiteral,
    data: {
        type: DataType,
        data: InternalDataRepresentation
    },
}

/**
 * Loops over any iterable object.
 * Example: `for item in array`
 */
export interface ForIter extends Interpretable {
    type: InterpType.ForIter,

    children: {
        /** The identifier for the loop counter (eg. `item`) */
        identifier: string,
        /** The object to loop over, (eg. `array`) */
        iterable: string,
        /** a BlockNode set to be executed for each loop */
        body: string
    }
}

/**
 * Loops a certain number of times using the start, step, and stop parameters.
 * 
 * While in Java and other languages, for loops are structured like 
 * `for(startingStatement, conditionalStatement, afterEveryLoopStatement)`, where it doesn't have to
 * just be iterating with a counter, here we want to enforce the loop coding style
 * `for(i = start; i < stop; i++).`
 */
export interface ForLoop extends Interpretable {
    type: InterpType.ForLoop,
    children: {
        /** The identifier for the loop counter (eg. `i`) */
        identifier: string
        /** What number to start the counter at */
        start?: string,
        /** What number to stop the counter at (using an implicit `counter < stop`) */
        stop: string,
        /** What number to increment each time */
        step?: string
        /** a BlockNode set to be executed for each loop */
        body: string
    }
}

/**
 * Represents a function definition, with an identifier, params, and body.
 */
export interface FunctionDef extends Interpretable {
    type: InterpType.FunctionDef,
    data: {
        /** For each child in `parameters` corresponds a type. Set dataType to "unknown" for an unknown type. */
        parameterTypes: DataType[]
    }
    children: {
        /** Identifier for the function name, eg. in `def main():` it would be `main` */
        identifier: string
        /** All parameters the function takes, a list of identifiers. */
        parameters: string[]
        /** a BlockNode set for each loop */
        body: string
    }
}

/**
 * Used for any user-defined name in their code.
 * 
 * Examples:
 * 
 * `call(x)` - `call` and `x` are identifiers.
 * `value = a + b`, `value`, `a` and `b` are identifiers.
 * 
 */
export interface Identifier extends Interpretable {
    type: InterpType.Identifier,
    data: {
        /** The identifier name. */
        name: string
    },
}

/**
 * Represents an If-Then statement, with an optional Else block.
 */
export interface If extends Interpretable {
    type: InterpType.If,
    children: {
        /** Should evaluate to either a truthy or falsy statement */
        condition: string,
        /** Will be evaluated if `condition` evaluates to truthy */
        then: string,
        /** Optional, will be evaluated if `condition` evaluates to falsy */
        else?: string
    }
}

/**
 * Marks a return from a Call, with optional data.
 * 
 * Handles both `return` and `return 'withReturnData'`.
 */
export interface Return extends Interpretable {
    type: InterpType.Return,
    children: {
        /** The data to return with */
        withData?: string
    }
}


/**
 * Handles indexing into an array.
 * 
 * Examples:
 * - `array[i]`
 *   - array is `array` and subscript is `i`
 * - `['a', 'b', 'c'][0]`
 *   - array is `['a', 'b', 'c']` and subscript is `0`
 *   - this would evaluate to `'a'`.
 * - `functionCall()[9]`
 *   - array is `functionCall()` as a CallNode and subscript is `9`
 *   - this assumes that the functionCall function will return an array.
 */
export interface Subscript extends Interpretable {
    type: InterpType.Subscript,
    children: {
        /** The array to use, could be a literal, a variable, or even a call. */
        array: string,
        /** What index to use in the array. */
        subscript: string
    }
}

/**
 * Any operation with one operand.
 * Examples:
 * - `!data`, operator is `!` and operand is `data`
 * - `-number`, operator is `-` and operand is `number`.
 * 
 */
export interface UnaryOperator extends Interpretable {
    type: InterpType.UnaryOp,
    data: {
        /**
         * Which operator to use.
         * - `!` is the not operator.
         * - `-` is a negation operator.
         * - `+` is a dummy operator (positive version of the - negation operator).
         */
        operator: '!' | '-' | '+'
    }
    children: {
        /** The Interpretable that the operator affects */
        operand: string
    }
}

/**
 * Represents a while loop.
 */
export interface While extends Interpretable {
    type: InterpType.While,
    children: {
        /** What to test for every loop. */
        condition: string,
        /** What to execute every loop after the `condition` passes. */
        body: string
    }
}

/**
 * A union type of all concrete Interpretables. This allows you to type narrow based on the 
 * `type` parameter in any Interpretable.
 * 
 * Example:
 * ```
 * const narrowed = (interpretable as NarrowedInterpretable)
 * if(narrowed.type == Assignment) {
 *      // narrowed is now properly typed to Assignment here.
 * }
 * ```
 */
export type NarrowedInterpretable =
    | ArrayLiteral
    | Assignment
    | BinaryGeneralOperator
    | BinaryNumericalOperator
    | Block
    | Break
    | Call
    | DataLiteral
    | ForIter
    | ForLoop
    | FunctionDef
    | If
    | Return
    | Subscript
    | UnaryOperator
    | Identifier
    | While;


export enum InterpType {
    ArrayLiteral = 'array_literal',
    Assignment = 'assignment',
    BinaryGeneralOp = 'binary_general_op',
    BinaryNumericalOp = 'binary_num_op',
    Block = 'block',
    Break = 'break',
    Call = 'call',
    DataLiteral = 'literal',
    ForIter = 'for_iter',
    ForLoop = 'for_loop',
    FunctionDef = 'function_def',
    Identifier = 'identifier',
    If = 'if',
    Return = 'return',
    Subscript = 'subscript',
    UnaryOp = 'unary_op',
    While = 'while',
}